import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn } from "@/shared/lib/cn";

type Tone = "brand" | "success" | "info" | "warning" | "danger" | "neutral";
type Variant = "solid" | "soft" | "tinted" | "outline" | "status" | "price";

/*
 * Colours per variant and tone. Written out in full: Tailwind only generates classes it finds literally in the
 * source, so `bg-${tone}` would produce nothing.
 */
const TONES: Record<Variant, Record<Tone, string>> = {
  /** Figma Badge Primary / Tag Primary. */
  solid: {
    brand: "bg-brand-500 text-white",
    success: "bg-success text-white",
    info: "bg-info text-white",
    warning: "bg-warning text-ink-strong",
    danger: "bg-danger text-white",
    neutral: "bg-ink-toned text-white",
  },
  /** Figma Badge Secondary: light background, coloured text. */
  soft: {
    brand: "bg-brand-50 text-brand-700",
    success: "bg-success/10 text-success",
    info: "bg-info/10 text-info",
    warning: "bg-warning/15 text-ink-toned",
    danger: "bg-danger/10 text-danger",
    neutral: "bg-canvas text-ink-toned",
  },
  /** Figma Tag Default / Status variant 1: light background, regular text. */
  tinted: {
    brand: "bg-brand-500/10 text-ink",
    success: "bg-success/10 text-ink",
    info: "bg-info/10 text-ink",
    warning: "bg-warning/10 text-ink",
    danger: "bg-danger/10 text-ink",
    neutral: "bg-canvas text-ink",
  },
  /** Figma Badge Outline: white background, full-colour border. */
  outline: {
    brand: "bg-surface text-brand-700 ring-brand-500",
    success: "bg-surface text-success ring-success",
    info: "bg-surface text-info ring-info",
    warning: "bg-surface text-ink-toned ring-warning",
    danger: "bg-surface text-danger ring-danger",
    neutral: "bg-surface text-ink ring-line",
  },
  /** Figma Status variant 2: white, grey border, a dot in the text colour. */
  status: {
    brand: "bg-surface text-brand-700 ring-line",
    success: "bg-surface text-success ring-line",
    info: "bg-surface text-info ring-line",
    warning: "bg-surface text-ink-toned ring-line before:bg-warning",
    danger: "bg-surface text-danger ring-line",
    neutral: "bg-surface text-ink ring-line",
  },
  /** Figma Badge Price / Tag Price: saturated background, dark text («… сум/мес»). */
  price: {
    brand: "bg-brand-400 text-ink-strong",
    success: "bg-success text-ink-strong",
    info: "bg-info/60 text-ink-strong",
    warning: "bg-highlight text-ink-strong",
    danger: "bg-danger text-ink-strong",
    neutral: "bg-line text-ink-strong",
  },
};

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center gap-1 font-medium whitespace-nowrap [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        solid: "",
        soft: "",
        tinted: "",
        outline: "ring-1 ring-inset",
        status:
          "ring-1 ring-inset before:size-1.5 before:shrink-0 before:rounded-full before:bg-current",
        price: "",
      },
      tone: { brand: "", success: "", info: "", warning: "", danger: "", neutral: "" },
      /** Figma xs 16 / sm 20 / md 24 / lg 28 px. */
      size: {
        xs: "h-4 rounded-sm px-1 text-body-micro [&_svg]:size-3",
        sm: "h-5 rounded-md px-1.5 text-body-sm [&_svg]:size-3",
        md: "h-6 rounded-md px-1.5 text-body-lg [&_svg]:size-3.5",
        lg: "h-7 rounded-lg px-2 text-title-xs [&_svg]:size-4",
      },
      /** Figma «Shape=Pill». */
      pill: { true: "rounded-full px-2" },
    },
    compoundVariants: (Object.keys(TONES) as Variant[]).flatMap((variant) =>
      (Object.keys(TONES[variant]) as Tone[]).map((tone) => ({
        variant,
        tone,
        className: TONES[variant][tone],
      })),
    ),
    defaultVariants: { variant: "solid", tone: "brand", size: "md" },
  },
);

type BadgeProps = ComponentProps<"span"> & VariantProps<typeof badgeVariants>;

/** Figma UI kit → Badge (536:4217), Tag (728:7981) and Status (148:3362) in one component. */
export function Badge({ className, variant, tone, size, pill, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant, tone, size, pill }), className)} {...props} />
  );
}
